import { ChevronRight } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { Wordmark } from './Wordmark';
import { ThemeToggle } from './ThemeToggle';

const isDev = import.meta.env.DEV;

interface NavItem {
  to: string;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'boards' },
  ...(isDev
    ? [
        { to: '/project', label: 'project' },
        { to: '/style-guide', label: 'style guide' },
      ]
    : []),
];

// Frame app header — addendum §"App-level header".
// 48px tall, --bg-app surface, 1px bottom border-subtle, left/right zones.
// Center zone is reserved for retro-mode phase pills (built later).
export function AppHeader() {
  const { pathname } = useLocation();
  const crumb = breadcrumbFor(pathname);

  return (
    <header
      className="
        flex items-center justify-between
        h-12 px-4 gap-3
        bg-bg-app border-b border-border-subtle
        relative z-[4]
      "
    >
      <div className="flex items-center gap-3 min-w-0">
        <Wordmark size="sm" />
        <span aria-hidden className="block w-px h-[18px] bg-border-default" />
        <Breadcrumb crumb={crumb} />
      </div>

      <nav className="flex items-center gap-1">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              [
                'px-2.5 h-control inline-flex items-center rounded-2',
                'text-13 transition-colors duration-micro ease-out',
                'focus-visible:outline-none focus-visible:shadow-focus',
                isActive
                  ? 'text-fg-primary bg-bg-subtle'
                  : 'text-fg-secondary hover:text-fg-primary hover:bg-bg-hover',
              ].join(' ')
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="flex items-center gap-2">
        <ThemeToggle />
      </div>
    </header>
  );
}

function Breadcrumb({ crumb }: { crumb: { workspace: string; current: string } }) {
  return (
    <div className="flex items-center gap-1 min-w-0 text-13">
      <span className="text-fg-tertiary truncate">{crumb.workspace}</span>
      <ChevronRight size={14} strokeWidth={1.5} className="text-fg-quaternary shrink-0" />
      <span className="text-fg-primary font-medium truncate">{crumb.current}</span>
    </div>
  );
}

function breadcrumbFor(pathname: string): { workspace: string; current: string } {
  if (pathname.startsWith('/b/')) {
    const id = pathname.slice(3) || 'untitled';
    return { workspace: 'workspace', current: id };
  }
  if (pathname.startsWith('/project')) return { workspace: 'workspace', current: 'project' };
  if (pathname.startsWith('/style-guide')) return { workspace: 'workspace', current: 'style guide' };
  return { workspace: 'workspace', current: 'home' };
}
